import logger from 'ws-scripts/modules/logger';
import getEnv from '../getEnv';
import mergeObjects from '../mergeObjects';
import replaceTemplateStrings from '../../mappings/forddirectnative/event/utils/replaceTemplateStrings';
import getVehicleTemplateStrings from '../../mappings/forddirectnative/event/utils/getVehicleTemplateStrings';

export const project = 'forddirectnative';

let templateStrings;
let initialData;

/**
 * Remove all colons that are not eventually followed by
 * text content ie. repeated colons and trailing colons.
 */
const regex = /:\s{0,}(?!\s?[^:])/g;
let adobeScriptLoaded = false;

// recursively search through an object's values and replace any that match above regex with an empty string
const traverseAndReplace = (obj) => {
	const traverse = (current) => {
		Object.keys(current).forEach((key) => {
			if (current[key]) {
				if (typeof current[key] === 'object' && current[key] !== null) {
					traverse(current[key]);
				} else if (typeof current[key] === 'string') {
					// eslint-disable-next-line no-param-reassign
					current[key] = current[key].replace(regex, '');
				}
			}
		});
	};

	traverse(obj);
	return obj;
};

const mergeVehicleData = (digitaldata, isShowroomOverview) => {
	let mergedDigitaldata = digitaldata;

	if (templateStrings.nameplate) {
		mergedDigitaldata = JSON.parse(JSON.stringify(digitaldata));
		mergedDigitaldata.vehicle = mergedDigitaldata.vehicle || {};
		mergedDigitaldata.vehicle.modelYear =
			mergedDigitaldata.vehicle?.modelYear || '{year}';
		mergedDigitaldata.vehicle.nameplate =
			mergedDigitaldata.vehicle?.nameplate || '{nameplate}';

		// resolve an issue where namplate is added twice due to it being in tracking tags
		// and the mapping file
		if (
			mergedDigitaldata?.page?.pageName &&
			!mergedDigitaldata.page.pageName.includes(
				mergedDigitaldata.vehicle?.nameplate
			) &&
			!mergedDigitaldata.page.pageName.includes(
				templateStrings.nameplate.toLowerCase()
			)
		) {
			mergedDigitaldata.page.pageName += ':{nameplate}';
		}

		if (!isShowroomOverview) {
			if (templateStrings.bodystyle && templateStrings.trim) {
				mergedDigitaldata.vehicle.bodyModelTrim =
					mergedDigitaldata.vehicle?.bodyModelTrim ||
					'{vehicleTechnology}:{bodystyle}:{trim}:{vehiclePerformance}';
			}

			if (
				templateStrings.exteriorColor &&
				templateStrings.interiorColor
			) {
				mergedDigitaldata.vehicle.extIntColorCode =
					'{exteriorColor}:{interiorColor}';
			}

			if (templateStrings.engine && templateStrings.transmission) {
				mergedDigitaldata.vehicle.engineTrans =
					'{engine}:{transmission}';
			}

			mergedDigitaldata.vehicle.price = '{vehiclePrice}';
		}
	}

	return mergedDigitaldata;
};

export const sending = (args) => {
	const { data, preview, event, eventData: _eventData, mapping } = args;

	// Event Viewer
	const reportsForEventViewer = [];
	const reportsForNewRelic = [];

	if (!preview && Object.keys(data).length) {
		if (event === 'ddc.wstagging.first') {
			initialData = data;
			templateStrings = getVehicleTemplateStrings();
			window.DDC.WidgetData[
				'ws-tagging'
			].mapping.forddirectnative.templateStrings = templateStrings;

			const {
				configuration: {
					defaultScriptLocation,
					liveScriptLocation
				} = {}
			} = mapping;

			window._satellite = window._satellite || {};
			window._satellite._monitors = window._satellite._monitors || [];
			window._satellite._monitors.push({
				ruleTriggered: (e) => logger.debug('rule triggered', e.rule),
				ruleCompleted: (e) => logger.debug('rule completed', e.rule),
				ruleConditionFailed: (e) =>
					logger.debug('rule condition failed', e.rule, e.condition)
			});

			window.digitaldata = replaceTemplateStrings(
				templateStrings,
				mergeVehicleData(
					data,
					window.DDC.dataLayer.page.pageInfo.isShowroomOverview
				)
			);

			// we need a copy of this for all future events
			window.pageLoadDigitaldata = JSON.parse(
				JSON.stringify(window.digitaldata)
			);

			// remove variantName as that is a page load only property
			if (window.pageLoadDigitaldata?.page?.variantName) {
				delete window.pageLoadDigitaldata.page.variantName;
			}
			if (initialData?.page?.variantName) {
				delete initialData.page.variantName;
			}

			const script = document.createElement('script');
			script.onload = () => {
				adobeScriptLoaded = true;
			};
			// you can remove the .min from here if you want to debug the adobe launch script more easily
			script.src = `https://assets.adobedtm.com/${
				getEnv() === 'prod' ? liveScriptLocation : defaultScriptLocation
			}.min.js`;
			script.async = true;

			document.head.appendChild(script);

			reportsForEventViewer.push({
				clientEventName: 'page load digitaldata',
				payload: window.digitaldata,
				sender: 'window.digitaldata'
			});

			reportsForNewRelic.push({
				clientEventName: 'page load digitaldata',
				sender: 'window.digitaldata'
			});
		} else if (Object.keys(data?.payload || {}).length || data.directCall) {
			const { linkType } = data;
			delete data.linkType;

			const scriptLoaded = new Promise((resolve) => {
				if (adobeScriptLoaded) {
					resolve();
				} else {
					const interval = setInterval(() => {
						if (adobeScriptLoaded) {
							clearInterval(interval);
							resolve();
						}
					}, 10);
				}
			});

			const { directCall = 'global-onclick', payload } = data;

			if (
				payload?.onclick?.onclick?.includes('sort by') ||
				payload?.onclick?.onclick?.includes('filter')
			) {
				templateStrings = getVehicleTemplateStrings();
				window.pageLoadDigitaldata = replaceTemplateStrings(
					templateStrings,
					mergeVehicleData(initialData, false)
				);
			}

			scriptLoaded.then(() => {
				window.digitaldata = mergeObjects(
					window.pageLoadDigitaldata, // we use the pageLoadDigitaldata as the starting point
					replaceTemplateStrings(
						{
							...templateStrings,
							// for forms that have vehicle data we need to get the nameplate added in
							...(payload?.vehicle?.nameplate && {
								nameplate: payload?.vehicle?.nameplate
							})
						},
						payload
					),
					true
				);

				if (
					window.digitaldata?.onclick?.onclickLinkName &&
					window.digitaldata.onclick.onclickLinkName !==
						'referral:exit'
				) {
					window.digitaldata.onclick.onclickLinkName = `dc:${window.digitaldata.onclick.onclickLinkName}`;
				}

				if (
					window.digitaldata?.page?.pageName &&
					!window.digitaldata?.page?.pageName?.startsWith?.('dc:')
				) {
					window.digitaldata.page.pageName = `dc:${window.digitaldata.page.pageName}`;
				}

				if (
					window.digitaldata?.page?.pageNameNoVehicle &&
					!window.digitaldata?.page?.pageNameNoVehicle?.startsWith?.(
						'dc:'
					)
				) {
					window.digitaldata.page.pageNameNoVehicle = `dc:${window.digitaldata.page.pageNameNoVehicle}`;
				}

				// sometimes there are double :: and this fixes that
				window.digitaldata = traverseAndReplace(window.digitaldata);

				window._satellite.track(directCall);
			});

			reportsForEventViewer.push(
				new Promise((resolve) => {
					scriptLoaded.then(() => {
						resolve({
							clientEventName: directCall,
							payload: window.digitaldata,
							sender: 'window.digitaldata'
						});
					});
				})
			);

			reportsForEventViewer.push(
				new Promise((resolve) => {
					scriptLoaded.then(() => {
						resolve({
							clientEventName: directCall,
							payload: directCall,
							sender: '_satellite.track'
						});
					});
				})
			);

			reportsForNewRelic.push(
				new Promise((resolve) => {
					scriptLoaded.then(() => {
						resolve({
							clientEventName: directCall,
							...(linkType && { linkType }),
							sender: '_satellite.track'
						});
					});
				})
			);
		}
	}

	return {
		reportsForEventViewer,
		reportsForNewRelic
	};
};
